<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'

import AsyncIcon from '@/atoms/AsyncIcon.vue'
import CtaButton from '@/atoms/CtaButton.vue'
import { Theme } from '@/types'
import { useAudio } from '@/utils/audio'

export default defineComponent({
  name: 'ButtonIcon',
  components: {
    AsyncIcon,
    CtaButton
  },
  props: {
    featured: { type: Boolean, default: false },
    icon: { type: String, default: undefined },
    small: { type: Boolean, default: false },
    theme: { type: String as PropType<`${Theme}`>, default: undefined },
    primary: { type: Boolean, default: true },
    tertiary: { type: Boolean, default: false }
  },
  setup () {
    const audio = useAudio('click')

    return { audio }
  }
})
</script>

<template>
  <CtaButton :class="{ featured }" tertiary :theme="theme">
    <template #content>
      <Transition v-if="icon" mode="out-in" name="icon">
        <AsyncIcon :key="icon" :name="icon" class="icon relative z-1" />
      </Transition>
      <slot />
    </template>
  </CtaButton>
</template>

<style lang="scss" scoped>
.cta.featured {
  width: rem(40px);
  min-width: initial !important;
  height: rem(40px);
  padding: 0 !important;

  &::before {
    left: rem(10px) !important;
    width: rem(20px) !important;
  }

  &::after {
    position: absolute;
    z-index: 0;
    top: 3px;
    left: 3px;
    width: calc(100% - 6px);
    height: calc(100% - 6px);
    border-radius: rem(10px);
    background-color: var(--cta-bg-color);
    content: '';
    transition: background-color $default-duration;
  }

  @include hover {
    &:hover::after {
      background-color: var(--cta-bg-hover-color, var(--cta-bg-color));
    }
  }

  &::v-deep(.cta-decoration) {
    top: rem(10px);
    width: rem(20px);
    height: rem(20px);
    border-radius: rem(4px);

    &::before {
      top: rem(-10px) !important;
    }

    &::after {
      bottom: rem(-10px) !important;
    }
  }
}

.icon {
  font-size: rem(18px);
}

.icon-enter-active,
.icon-leave-active {
  transition: transform $default-duration, opacity $default-duration;
}

.icon-enter-from,
.icon-leave-to {
  opacity: 0;
  transform: scale(0.5);
}
</style>
